import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { orderTrackingService } from '../../services/orderTrackingService';
import { adminOrderService } from '../../services/adminOrderService';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/LoadingSpinner';
import Icon from '../../components/AppIcon';
import CreateShipmentModal from './components/CreateShipmentModal';
import CarrierManagement from './components/CarrierManagement';
import BulkShippingPanel from './components/BulkShippingPanel';
import ShippingStats from './components/ShippingStats';

const ShippingManagementCenter = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    if (userProfile?.role !== 'admin') {
      navigate('/order-tracking-dashboard');
      return;
    }

    loadData();
  }, [user, userProfile, navigate]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [ordersData, shipmentsData, carriersData] = await Promise.all([
        adminOrderService?.getAllOrders(),
        orderTrackingService?.getAllShipments(),
        orderTrackingService?.getCarriers()
      ]);

      setOrders(ordersData || []);
      setShipments(shipmentsData || []);
      setCarriers(carriersData || []);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateShipment = (order) => {
    setSelectedOrder(order);
    setShowCreateModal(true);
  };

  const handleShipmentCreated = () => {
    setShowCreateModal(false);
    setSelectedOrder(null);
    loadData();
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => 
      prev?.includes(orderId)
        ? prev?.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const getPendingOrders = () => {
    const shippedOrderIds = shipments?.map(s => s?.orderId);
    return orders?.filter(o => 
      !shippedOrderIds?.includes(o?.id) && 
      (o?.status === 'confirmed' || o?.status === 'processing')
    );
  };

  const getOrderStatus = (orderId) => {
    const shipment = shipments?.find(s => s?.orderId === orderId);
    return shipment?.currentStatus || 'pending';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'Kargoya Hazırlanıyor', className: 'bg-yellow-100 text-yellow-800' },
      confirmed: { label: 'Onaylandı', className: 'bg-blue-100 text-blue-800' },
      processing: { label: 'İşleniyor', className: 'bg-purple-100 text-purple-800' },
      shipped: { label: 'Kargoya Verildi', className: 'bg-green-100 text-green-800' },
      delivered: { label: 'Teslim Edildi', className: 'bg-emerald-100 text-emerald-800' }
    };

    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    })?.format(price);
  };

  const pendingOrders = getPendingOrders();

  if (loading) {
    return (
      <>
        <Header />
        <div className="main-content bg-background min-h-screen">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
            <div className="bg-card rounded-xl shadow-organic p-8">
              <LoadingSpinner />
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="main-content bg-background min-h-screen">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
              Kargo Yönetim Merkezi
            </h1>
            <p className="text-muted-foreground">
              Sipariş kargolama, taşıyıcı yönetimi ve toplu işlemler
            </p>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Stats */}
          <ShippingStats 
            orders={orders}
            shipments={shipments}
            carriers={carriers}
          />

          {/* Tabs */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="flex gap-4">
                <button
                  onClick={() => setActiveTab('pending')}
                  className={`px-4 py-3 border-b-2 font-medium transition-colors ${
                    activeTab === 'pending' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Kargoya Hazır ({pendingOrders?.length})
                </button>
                <button
                  onClick={() => setActiveTab('shipped')}
                  className={`px-4 py-3 border-b-2 font-medium transition-colors ${
                    activeTab === 'shipped' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Kargoda ({shipments?.filter(s => s?.currentStatus === 'in_progress')?.length})
                </button>
                <button
                  onClick={() => setActiveTab('carriers')}
                  className={`px-4 py-3 border-b-2 font-medium transition-colors ${
                    activeTab === 'carriers' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Kargo Firmaları
                </button>
                <button
                  onClick={() => setActiveTab('bulk')}
                  className={`px-4 py-3 border-b-2 font-medium transition-colors ${
                    activeTab === 'bulk' ?'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Toplu İşlemler
                </button>
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'pending' && (
            <div className="space-y-4">
              {pendingOrders?.length === 0 ? (
                <div className="bg-card rounded-xl p-12 text-center border border-border">
                  <Icon name="Package" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Kargoya Hazır Sipariş Yok
                  </h3>
                  <p className="text-muted-foreground">
                    Tüm siparişler kargoya verilmiş durumda
                  </p>
                </div>
              ) : (
                pendingOrders?.map((order) => (
                  <div 
                    key={order?.id}
                    className="bg-card rounded-xl p-6 border border-border hover:shadow-organic transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-foreground">
                            Sipariş #{order?.id?.substring(0, 8)}
                          </h3>
                          {getStatusBadge(order?.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {format(new Date(order?.createdAt), 'dd MMMM yyyy, HH:mm', { locale: tr })}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCreateShipment(order)}
                        className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
                      >
                        <Icon name="Truck" size={18} />
                        <span>Kargoya Ver</span>
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Tutar</p>
                        <p className="text-sm font-medium text-foreground">
                          {formatPrice(order?.totalAmount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Ürün Sayısı</p>
                        <p className="text-sm font-medium text-foreground">
                          {order?.items?.length} ürün
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Teslimat Adresi</p>
                        <p className="text-sm font-medium text-foreground truncate">
                          {order?.shippingAddress?.city || 'Belirtilmedi'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'shipped' && (
            <div className="space-y-4">
              {shipments?.filter(s => s?.currentStatus === 'in_progress')?.length === 0 ? (
                <div className="bg-card rounded-xl p-12 text-center border border-border">
                  <Icon name="Truck" size={48} className="mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    Kargoda Gönderi Yok
                  </h3>
                  <p className="text-muted-foreground">
                    Şu anda yolda olan gönderi bulunmuyor
                  </p>
                </div>
              ) : (
                shipments
                  ?.filter(s => s?.currentStatus === 'in_progress')
                  ?.map((shipment) => (
                    <div 
                      key={shipment?.id}
                      className="bg-card rounded-xl p-6 border border-border"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-foreground mb-2">
                            {shipment?.trackingNumber}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            Sipariş: #{shipment?.orderId?.substring(0, 8)}
                          </p>
                        </div>
                        {getStatusBadge(shipment?.currentStatus)}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Kargo Firması</p>
                          <p className="text-sm font-medium text-foreground">
                            {shipment?.carrier}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Mevcut Konum</p>
                          <p className="text-sm font-medium text-foreground">
                            {shipment?.currentLocation || 'Bilgi bekleniyor'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-muted-foreground mb-1">Tahmini Teslimat</p>
                          <p className="text-sm font-medium text-foreground">
                            {shipment?.estimatedDeliveryDate
                              ? format(new Date(shipment?.estimatedDeliveryDate), 'dd MMM yyyy', { locale: tr })
                              : 'Belirtilmedi'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          )}

          {activeTab === 'carriers' && (
            <CarrierManagement carriers={carriers} onUpdate={loadData} />
          )}

          {activeTab === 'bulk' && (
            <BulkShippingPanel 
              orders={pendingOrders}
              carriers={carriers}
              onComplete={loadData}
            />
          )}
        </div>
      </div>

      {/* Create Shipment Modal */}
      {showCreateModal && selectedOrder && (
        <CreateShipmentModal
          order={selectedOrder}
          carriers={carriers}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedOrder(null);
          }}
          onSuccess={handleShipmentCreated}
        />
      )}
    </>
  );
};

export default ShippingManagementCenter;